/**
 * IRLDatabookAdder — reads the agenda-pinned databook, extracts questions
 * marked with "x" in the IRL column, and lets the user review / edit
 * candidates before appending them to the project's IRL.
 *
 * Section attribution (Revenue, COGS, OpEx, etc.) is resolved server-side via
 * /api/agenda/irl-questions, which uses ExcelJS outline levels — the same
 * mechanism the agenda builder uses. GL numbers are read client-side from
 * column D (a simple cell read, no outline levels needed).
 */

import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import type { IRLPriority, IRLRequest } from "../types";
import { getIRLMeta, getIRLRequests, saveIRLRequests } from "../store/irlStore";
import { getAgendaDatabookPin, loadDatabookBlob } from "../../fdd/tools/agenda/storage";
import { apiFetch } from "../../api/fddClient";

type Props = {
  projectId: string;
  onAdded: (addedIds: string[]) => void;
  onCancel: () => void;
};

type CandidateRow = {
  rowId: string;
  number: string;
  topic: string;
  entityName: string;
  request: string;
  priority: IRLPriority;
  sourceNote: string;
};

type ServerItem = {
  section: string;
  lineItem: string;
  question: string;
  tab: string;
  rowNum: number; // 1-based (ExcelJS row index)
};

const PRIORITY_OPTIONS: { value: IRLPriority; label: string; bg: string; fg: string }[] = [
  { value: "high",   label: "High",   bg: "#fee2e2", fg: "#991b1b" },
  { value: "medium", label: "Medium", bg: "#fef9c3", fg: "#854d0e" },
  { value: "low",    label: "Low",    bg: "#e0f2fe", fg: "#075985" },
];

function nextStartingNumber(requests: IRLRequest[]): number {
  let max = 0;
  for (const r of requests) {
    const m = /^\s*(\d+)/.exec(r.number ?? "");
    if (m) {
      const n = parseInt(m[1], 10);
      if (!isNaN(n) && n > max) max = n;
    }
  }
  return max + 1;
}

function cellStr(sheet: XLSX.WorkSheet, r: number, c: number): string {
  const cell = sheet[XLSX.utils.encode_cell({ r, c })];
  if (!cell || cell.v == null) return "";
  return String(cell.v).trim();
}

const IRLDatabookAdder: React.FC<Props> = ({ projectId, onAdded, onCancel }) => {
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [rows, setRows]           = useState<CandidateRow[]>([]);
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => { void extract(); }, [projectId]);

  async function extract() {
    setLoading(true);
    setError(null);
    try {
      const pin = getAgendaDatabookPin(projectId);
      if (!pin) {
        setError("No databook pinned. Pin a databook in the Agenda Builder first.");
        return;
      }

      const hasIrlCols = Object.values(pin.irlColByTab ?? {}).some((v) => v != null);
      if (!hasIrlCols) {
        setError("This databook doesn't have an IRL column yet. Rebuild the databook to get the Agenda and IRL routing columns.");
        return;
      }

      const buf = await loadDatabookBlob(projectId);
      if (!buf) {
        setError("Databook bytes missing — re-pin the databook and try again.");
        return;
      }

      // Parse client-side only for GL number lookups (column D — reliable cell read).
      const workbook = XLSX.read(buf, { type: "array" });

      // Send to server for section-aware extraction using ExcelJS outline levels.
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const form = new FormData();
      form.append("databook",         blob, pin.fileName);
      form.append("questionColByTab", JSON.stringify(pin.questionColByTab));
      form.append("irlColByTab",      JSON.stringify(pin.irlColByTab ?? {}));
      form.append("headerRowByTab",   JSON.stringify(pin.headerRowByTab));
      form.append("sheetNameByTab",   JSON.stringify(pin.sheetNameByTab ?? {}));

      const res = await apiFetch("/api/agenda/irl-questions", { method: "POST", body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err?.error || `Server error (HTTP ${res.status})`);
        return;
      }

      const data = await res.json() as { items: ServerItem[] };
      const items = Array.isArray(data.items) ? data.items : [];

      if (items.length === 0) {
        setError("No questions marked for IRL. Open the databook and put an x in the IRL column next to any question you want to import.");
        return;
      }

      const existing  = getIRLRequests(projectId);
      let startNum    = nextStartingNumber(existing);

      const candidates: CandidateRow[] = items.map((item, i) => {
        // GL number lives in column D (index 3). rowNum is 1-based from ExcelJS,
        // so subtract 1 for the 0-based XLSX.js index.
        const actualSheet = (pin.sheetNameByTab ?? {})[item.tab] || item.tab;
        const sheet       = workbook.Sheets[actualSheet];
        const glNum       = sheet ? cellStr(sheet, item.rowNum - 1, 3) : "";

        const accountLabel  = glNum ? `${item.lineItem} (${glNum})` : item.lineItem;
        const requestHeader = item.section && item.section !== item.lineItem
          ? `${item.section} — ${accountLabel}`
          : accountLabel;

        return {
          rowId:      `db-${item.tab}-${item.rowNum}-${i}`,
          number:     String(startNum++),
          topic:      "",
          entityName: "",
          request:    `${requestHeader}\n${item.question}`,
          priority:   "medium" as IRLPriority,
          sourceNote: `${item.tab} · row ${item.rowNum}`,
        };
      });

      setRows(candidates);
      setSelected(new Set(candidates.map((c) => c.rowId)));
    } catch (e: any) {
      setError(e?.message || "Failed to load databook.");
    } finally {
      setLoading(false);
    }
  }

  const updateRow = (rowId: string, patch: Partial<CandidateRow>) =>
    setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));

  const toggleRow = (rowId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });

  const allSelected  = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll    = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.rowId)));

  const handleSave = () => {
    if (selected.size === 0) { setSaveError("Select at least one item to add."); return; }
    const existing  = getIRLRequests(projectId);
    const meta      = getIRLMeta(projectId);
    const fileName  = meta?.originalFileName || "Databook-derived IRL";
    const additions: IRLRequest[] = rows
      .filter((r) => selected.has(r.rowId))
      .map((r, i) => ({
        id:         `db-${Date.now()}-${i}`,
        number:     r.number.trim() || String(nextStartingNumber(existing) + i),
        topic:      r.topic.trim()      || undefined,
        entityName: r.entityName.trim() || undefined,
        request:    r.request.trim(),
        status:     "open" as const,
        priority:   r.priority,
      }));
    saveIRLRequests(projectId, [...existing, ...additions], fileName);
    onAdded(additions.map((a) => a.id));
  };

  /* ── Loading / error states ── */
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: 13, color: "#4a7d4a", padding: 16, textAlign: "center" }}>
          Reading databook…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#991b1b" }}>{error}</div>
          <button onClick={onCancel} style={secondaryBtn}>Close</button>
        </div>
      </div>
    );
  }

  /* ── Review page ── */
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <div style={{ fontWeight: 700, color: "#2f6b2f", fontSize: 14 }}>
            📥 Import trending questions from databook
          </div>
          <div style={{ fontSize: 12, color: "#4a7d4a", marginTop: 2 }}>
            {rows.length} candidate{rows.length === 1 ? "" : "s"} · {selected.size} selected · edit any field before adding
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={secondaryBtn}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={selected.size === 0}
            style={{
              ...primaryBtn,
              background: selected.size === 0 ? "#9bbf8f" : "#15803d",
              cursor: selected.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            ✅ Add {selected.size} to IRL
          </button>
        </div>
      </div>

      <div style={selectAllBarStyle}>
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={toggleAll}
          style={{ cursor: "pointer", margin: 0 }}
        />
        <span>{allSelected ? "Uncheck all" : "Select all"}</span>
        <span style={{ marginLeft: "auto", color: "#6b7280" }}>
          All IRL-marked questions are pre-selected. Uncheck any you don't want.
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
        {rows.map((r) => {
          const isOn    = selected.has(r.rowId);
          const prioOpt = PRIORITY_OPTIONS.find((o) => o.value === r.priority) ?? PRIORITY_OPTIONS[1];
          return (
            <div
              key={r.rowId}
              style={{
                border: `1px solid ${isOn ? "#cfe6c7" : "#e5e7eb"}`,
                borderRadius: 10, padding: 12,
                background: isOn ? "#fbfffa" : "#f9fafb",
                opacity: isOn ? 1 : 0.7,
                display: "flex", gap: 12,
              }}
            >
              <div style={{ paddingTop: 4 }}>
                <input
                  type="checkbox" checked={isOn} onChange={() => toggleRow(r.rowId)}
                  style={{ cursor: "pointer", margin: 0, width: 16, height: 16 }}
                />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <FieldLabel label="IRL #" width={80}>
                    <input
                      type="text" value={r.number}
                      onChange={(e) => updateRow(r.rowId, { number: e.target.value })}
                      style={{ ...rowInputStyle, fontFamily: "monospace", width: "100%" }}
                    />
                  </FieldLabel>
                  <FieldLabel label="Topic" width={170}>
                    <input
                      type="text" value={r.topic} placeholder="—"
                      onChange={(e) => updateRow(r.rowId, { topic: e.target.value })}
                      style={{ ...rowInputStyle, width: "100%" }}
                    />
                  </FieldLabel>
                  <FieldLabel label="Entity" width={200}>
                    <input
                      type="text" value={r.entityName} placeholder="(none)"
                      onChange={(e) => updateRow(r.rowId, { entityName: e.target.value })}
                      style={{ ...rowInputStyle, width: "100%" }}
                    />
                  </FieldLabel>
                  <FieldLabel label="Priority" width={110}>
                    <select
                      value={r.priority}
                      onChange={(e) => updateRow(r.rowId, { priority: e.target.value as IRLPriority })}
                      style={{ ...rowInputStyle, width: "100%", background: prioOpt.bg, color: prioOpt.fg, fontWeight: 700 }}
                    >
                      {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </FieldLabel>
                </div>
                <FieldLabel label="Request">
                  <textarea
                    value={r.request}
                    onChange={(e) => updateRow(r.rowId, { request: e.target.value })}
                    rows={3}
                    style={{ ...rowInputStyle, width: "100%", resize: "vertical", minHeight: 56, lineHeight: 1.4, fontFamily: "inherit" }}
                  />
                </FieldLabel>
                {r.sourceNote && (
                  <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>
                    source: {r.sourceNote}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {saveError && <div style={{ ...errorStyle, marginTop: 12 }}>{saveError}</div>}
    </div>
  );
};

/* ── Styles ── */

const containerStyle: React.CSSProperties = {
  background: "#ffffff", border: "1px solid #cfe6c7",
  borderRadius: 10, padding: 14,
  display: "flex", flexDirection: "column", gap: 10,
};

const headerStyle: React.CSSProperties = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", gap: 12, flexWrap: "wrap",
};

const selectAllBarStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "6px 10px", background: "#f4fbf1",
  border: "1px solid #cfe6c7", borderRadius: 6,
  fontSize: 12, color: "#4a7d4a",
};

const primaryBtn: React.CSSProperties = {
  background: "#15803d", color: "#fff", border: "none",
  borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12,
};

const secondaryBtn: React.CSSProperties = {
  background: "#fff", color: "#4a7d4a", border: "1px solid #cfe6c7",
  borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600,
};

const rowInputStyle: React.CSSProperties = {
  padding: "5px 8px", border: "1px solid #cfe6c7",
  borderRadius: 5, fontSize: 12, background: "#fff", color: "#333", boxSizing: "border-box",
};

const errorStyle: React.CSSProperties = {
  background: "#fef2f2", border: "1px solid #fca5a5",
  borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#991b1b",
};

const FieldLabel: React.FC<{ label: string; width?: number; children: React.ReactNode }> = ({ label, width, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 2, width: width ?? "auto", flex: width ? "0 0 auto" : 1 }}>
    <span style={{ fontSize: 10, fontWeight: 700, color: "#4a7d4a", textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </span>
    {children}
  </label>
);

export default IRLDatabookAdder;
